import { messageOf } from '../../lib/errors'
import { startViewAs } from '../../lib/viewAs'
import { useEffect, useState } from 'react'
import { INVITE_OUTCOME_LABEL, inviteExistingPerson, sendInvitations, sendSignInCode } from '../../lib/invites'
import {
  GRANT_CATALOGUE,
  ROLE_GROUPS,
  assignRole,
  catalogueEntry,
  holdsPrivilegedRole,
  isRegional,
  revokeRole,
  scopeOptionsFor,
  type PeopleScope,
  type PersonRoles,
} from '../../lib/roles'
import { addRoomManager, removeRoomManager } from '../../lib/shop'
import { STATE_LABEL, STATE_TONE } from './PeopleTable'
import { initialsOf } from '../../lib/initials'
import {
  loadPersonDetail,
  setMembershipActive,
  type OrgUnitOption,
  type PersonDetail,
} from '../../lib/admin'
import { ProfileEditor } from './ProfileEditor'
import { RoleRow } from './RoleRow'
import type { BuPlace } from '../../lib/where'
import { StoreRow, type StoreOption } from './StoreRow'

/**
 * One person: their access, their sign-in addresses, where they sit, and the
 * roles they hold.
 *
 * ── EVERY GUARD HERE IS THE SERVER'S ────────────────────────────────────────
 *
 * assign_role, revoke_role and set_membership_active are SECURITY DEFINER, so
 * the checks inside them are the entire authorisation boundary rather than a
 * second line of defence — that is documented at length in 021 and 027 and is
 * worth reading before changing anything here. This component disables a
 * control here and there to save a pointless round trip, and shows the server's
 * own message when it refuses. Those messages are written to be read by a
 * person and each says what to do instead, so replacing them with "Something
 * went wrong" would throw away the useful half.
 *
 * ── SUSPENSION IS NOT A ROLE ────────────────────────────────────────────────
 *
 * There is no employee toggle, and there must not be. 021 grants that role by
 * trigger on tenant membership and revoke_role refuses it outright, because a
 * direct revoke would be undone by the next reactivation while appearing to
 * have worked. Removing somebody's access is deactivating their membership,
 * which is a different and more honest act — and the one the button below does.
 *
 * ── THE ORDER OF THE SECTIONS IS A DECISION (11 Sep, David) ─────────────────
 *
 * Profile, then Roles, then Sign-in, then Access. Roles used to come first and
 * took most of the height for the thing that changes least, while job title,
 * business unit, department and — since 055 — the work email sat beneath it.
 *
 * It also reads as the order somebody is set up in: who they are and where they
 * sit, what extra they may do, how they get in, and whether they still should.
 * Sign-in stays BELOW the profile despite the email being in both, because the
 * profile's address is a contact detail and this one is a boundary. Two
 * addresses, two meanings, and the distance between them on screen is doing
 * some of the work of keeping them apart.
 *
 * ── IN A TRAY SINCE 137 (9 Oct, David: "like we did for store items") ───────
 *
 * The panel used to open as a row inside the table, under the person. It now
 * renders inside a right-hand `Dialog variant="tray"` (Admin.tsx), the same
 * surface as the store's item tray (REC-65), so the roster stays where it was
 * behind it. The dialog carries the name and the close button; this component
 * opens with a summary instead of a second copy of both.
 *
 * ── WHAT AN HR ADMINISTRATOR MAY NOT TOUCH (137) ────────────────────────────
 *
 * Without admin.roles.privileged (`canPrivileged` false), the Tenant and HR
 * Administrator rows are locked, and so is EVERY role row and the access
 * control for somebody who holds either of those roles — including the viewer
 * themselves. app.require_privileged_for refuses all of it server-side; the
 * locks here say why before anybody presses anything.
 *
 * ── EVERY GRANT, GROUPED (140) ──────────────────────────────────────────────
 *
 * The roles section renders lib/roles.ts's GRANT_CATALOGUE — all ten roles and
 * the store-manager list — under four headings, and then anything the person
 * holds that the catalogue does not know, under "Other grants". It must never
 * go back to iterating a short list: until 140 that silently dropped five of
 * the tenant administrator's roles and every store a manager ran.
 *
 * Store manager is its own row (StoreRow), because its scope is a STORE and
 * its writes are add/remove_stock_room_manager. The lock for it is 137's,
 * with one difference the server also makes: a shop administrator may change
 * anybody's stores (app.require_store_manager_authority), so `canShopAdmin`
 * lifts it.
 *
 * ── A REGIONAL HR ADMINISTRATOR (153) ───────────────────────────────────────
 *
 * With `scope` regional, the panel only ever opens on somebody in the region
 * (people_roster lists nobody else). Every role but the two privileged ones may
 * be granted (David, 10 Oct: "they can give the other grants to people in
 * their scope"); a scopable role (Comms Administrator) only for a unit at or
 * under the region, so its whole-company button gives way to a line saying
 * whose that grant is, and its picker lists the region's units alone. The
 * server draws the same line (app.require_grant_in_scope).
 */
export function PersonPanel({
  person,
  orgUnits,
  colleagues,
  myPersonId,
  activeAdminCount,
  canPrivileged = true,
  canEditProfile = true,
  canCreateOrgUnit = true,
  canShopAdmin = false,
  canViewAs = false,
  stores = [],
  scope = null,
  typicalHolders = {},
  places = [],
  onChanged,
}: {
  person: PersonRoles
  orgUnits: OrgUnitOption[]
  /**
   * Everyone the roster returned, for the reporting line. Passed down rather
   * than fetched here: the table has already paid for this list, and a second
   * read of ~900 people to fill one select is the kind of thing that makes an
   * admin screen slow for no reason anyone can see.
   */
  colleagues: { personId: string; displayName: string }[]
  myPersonId: string
  activeAdminCount: number
  /** admin.roles.privileged — the tenant administrator. See the header. */
  canPrivileged?: boolean
  /** people.update or admin.org — may edit the employment record. */
  canEditProfile?: boolean
  /** admin.org — may add a department while editing. HR may not (137). */
  canCreateOrgUnit?: boolean
  /** 140. shop.admin — may change anybody's stores, privileged or not. */
  canShopAdmin?: boolean
  /** 176. people.view_as — may see the platform as this person, read-only. */
  canViewAs?: boolean
  /** 140. Every store in the tenant, for the store-manager picker. */
  stores?: StoreOption[]
  /** 153. Whose list this is. null or wide: the whole company. */
  scope?: PeopleScope | null
  /** 154. Confirmed typical holders' words by role key, printed beside each role. */
  typicalHolders?: Record<string, { name: string; why: string }[]>
  /** 159. Each business unit's country: a scoped grant can be made for a country. */
  places?: BuPlace[]
  onChanged: () => Promise<void> | void
  /** Unused since 137 — the tray's own × closes it. Kept so callers compile. */
  onClose?: () => void
}) {
  const [detail, setDetail] = useState<PersonDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [confirmSuspend, setConfirmSuspend] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteOutcome, setInviteOutcome] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setDetail(null)
    setError(null)
    setConfirmSuspend(false)
    setInviteEmail('')
    setInviteOutcome(null)
    loadPersonDetail(person.personId)
      .then((d) => {
        if (!live) return
        setDetail(d)
        // Prefill the invitation from the profile's work email (055). The whole
        // point of storing it: Bamboo knows an address for 880 of the 931
        // active staff and the hub was throwing every one of them away, so
        // inviting somebody meant retyping what the database had been handed.
        //
        // Only when there is no identity yet — once somebody is invited this
        // box is not on screen, and prefilling a box that is about to become a
        // CHANGE of sign-in address would be offering ACC-21, which is not
        // built and is not this screen's decision to make.
        if (d.identities.length === 0 && d.workEmail) setInviteEmail(d.workEmail)
      })
      .catch((e: Error) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [person.personId])

  async function run(token: string, fn: () => Promise<unknown>) {
    setBusy(token)
    setError(null)
    try {
      await fn()
      await onChanged()
      setDetail(await loadPersonDetail(person.personId))
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const isMe = person.personId === myPersonId
  const isLastAdmin =
    person.roleKeys.includes('tenant_admin') && activeAdminCount <= 1
  // 137. Somebody who administers people is the tenant administrator's to
  // change. Includes the viewer: an HR administrator cannot grant themselves
  // an editorial role, which is the escalation this exists to stop.
  const personLocked = !canPrivileged && holdsPrivilegedRole(person)
  // 153. A regional HR Administrator.
  const regional = isRegional(scope)
  // 153. Where a regional holder may place somebody: their region's entities
  // and units, and any department (departments span entities). set_employment
  // refuses a move out of the region; this keeps it off the picker.
  const profileUnits = isRegional(scope)
    ? orgUnits.filter((o) => o.type === 'department' || scope.unitIds.includes(o.orgUnitId))
    : orgUnits
  // 140. Held, but not in the catalogue. The baseline is excluded because the
  // note below the roles already says everyone holds it.
  const otherGrants = person.grants.filter((g) => g.key !== 'employee' && !catalogueEntry(g.key))
  // The invitation is for somebody who is not in yet. Active, unverified and
  // inactive people get no button — the server would refuse them anyway.
  const canInvite =
    person.membershipActive && (person.accessStatus === 'not_invited' || person.accessStatus === 'invited')
  const protectedName = person.roleKeys.includes('tenant_admin')
    ? 'a Tenant Administrator'
    : 'an HR Administrator'
  const lockedNote = isMe
    ? 'Only a Tenant Administrator can change your own roles or access.'
    : `${person.displayName} is ${protectedName}. Only a Tenant Administrator can change their roles or access.`

  return (
    <div className="panel panel--tray" role="region" aria-label={`Manage ${person.displayName}`}>
      {/* ── SUMMARY ───────────────────────────────────────────────────────────
          Who this is at a glance: title, status, where they sit. The tray's
          header already carries the name. */}
      <header className="personsum">
        <span className="avatar personsum__avatar" aria-hidden="true">
          {initialsOf(person.displayName)}
        </span>
        <div className="personsum__text">
          <p className="personsum__title">
            {person.jobTitle ?? 'No job title'}
            {isMe && <span className="roles__flag roles__flag--you">you</span>}
          </p>
          <p className="panel__sub">
            {person.locale}
            {detail?.timezone && ` · ${detail.timezone}`}
          </p>
        </div>
        <span className={`statuspill statuspill--${STATE_TONE[person.accessStatus]}`}>
          {STATE_LABEL[person.accessStatus]}
        </span>
      </header>

      {(person.legalEntityName || person.businessUnitName || person.departmentName) && (
        <ul className="personsum__where" aria-label="Where they sit">
          {person.legalEntityName && (
            <li className="tagpill"><span className="tagpill__of">Entity</span>{person.legalEntityName}</li>
          )}
          {person.businessUnitName && (
            <li className="tagpill"><span className="tagpill__of">BU</span>{person.businessUnitName}</li>
          )}
          {person.departmentName && (
            <li className="tagpill"><span className="tagpill__of">Dept</span>{person.departmentName}</li>
          )}
        </ul>
      )}

      {/* 176. View as. Anyone in the tenant but yourself (David, 12 Oct),
          administrators included, suspended people included — what a
          suspended person sees is the no-access page, and that is worth
          checking too. The reload that follows lands on their home. */}
      {canViewAs && !isMe && (
        <div className="panel__inviteline viewas__start">
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy !== null}
            title="See the platform as this person, read-only, for up to an hour. Recorded."
            onClick={() => {
              setBusy('viewas')
              setError(null)
              startViewAs(person.personId).catch((e) => {
                setError(messageOf(e))
                setBusy(null)
              })
            }}
          >
            {busy === 'viewas' ? '…' : `View as ${person.displayName}`}
          </button>
          <span className="panel__sub">Read-only, up to an hour, recorded.</span>
        </div>
      )}

      {personLocked && (
        <p className="panel__lock" role="note">
          {lockedNote} HR administrators can still edit the profile.
        </p>
      )}

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* ── SIGN-IN, FIRST (David, 9 Oct: "it's the most common action") ──────
          Above the profile now. Send invitation is the primary act: the
          backend invitation email (138), which makes the identity from the
          Bamboo work address itself. Typing an address and sending a code is
          the fallback for somebody with no address on file. */}
      <section className="panel__section">
        <h3 className="panel__h3">Sign-in</h3>
        {canInvite && (
          <div className="panel__inviteline">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busy !== null}
              onClick={() =>
                run('invitation', async () => {
                  const [r] = await sendInvitations([person.personId])
                  setInviteOutcome(
                    r?.outcome === 'queued'
                      ? 'Invitation sent.'
                      : `Not sent — ${r ? INVITE_OUTCOME_LABEL[r.outcome] : 'no answer'}.`,
                  )
                })
              }
            >
              {busy === 'invitation' ? 'Sending…' : person.accessStatus === 'invited' ? 'Send invitation again' : 'Send invitation'}
            </button>
            {inviteOutcome && (
              <span className="admin__hint" role="status">
                {inviteOutcome}
              </span>
            )}
          </div>
        )}
        {detail === null && !error && <p className="empty" aria-busy="true">Loading…</p>}
        {detail && detail.identities.length === 0 && (
          // With a work email on file, Send invitation above is the act and this
          // is the exception (a different address, a code right now), folded.
          // With none, it is the only way in, so it is open.
          <details className="panel__invite panel__alt" open={!detail.workEmail}>
            <summary className="panel__altsum">
              {detail.workEmail
                ? 'Use a different address and send a code now'
                : 'No work email on file — invite an address'}
            </summary>
            <label className="admin__field">
              <span className="admin__searchlabel">Invite this address</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@conetec.com"
                autoComplete="off"
              />
              {/* Prefilled from the profile's work email (055), which is why
                  that field exists: BambooHR knows an address for 880 of the
                  931 active staff, and before 055 an administrator would have
                  had to retype every one of them. It is a SUGGESTION — sending
                  the code is still a deliberate press, because this is the act
                  that lets somebody in. */}
              {detail.workEmail && inviteEmail === detail.workEmail && (
                <span className="admin__hint">From their profile.</span>
              )}
            </label>
            <button
              type="button"
              className={detail.workEmail ? 'btn btn--quiet btn--sm' : 'btn btn--primary btn--sm'}
              disabled={busy !== null || inviteEmail.trim() === ''}
              onClick={() =>
                run('invite', async () => {
                  await inviteExistingPerson(person.personId, inviteEmail)
                  await sendSignInCode(person.personId, inviteEmail)
                  setInviteEmail('')
                })
              }
            >
              {busy === 'invite' ? 'Inviting…' : 'Invite and send a code'}
            </button>
          </details>
        )}
        {detail && detail.identities.length > 0 && (
          <ul className="panel__identities">
            {detail.identities.map((i) => (
              <li key={`${i.provider}:${i.identifier}`}>
                <span className="mono">{i.identifier}</span>
                <span className="admin__muted"> · {i.provider}</span>
                {i.isPrimary && <span className="chip chip--hint">primary</span>}
                {/* `linked` and `isVerified` both mean "a code was once sent to
                    this address" — 010's trigger sets them at the auth.users
                    INSERT, which happens when the code is REQUESTED. Neither
                    says the person ever signed in; the roster's Access column
                    is the honest answer to that (045). */}
                {i.linked ? (
                  <span className="admin__state admin__state--ok">code sent</span>
                ) : (
                  <span className="admin__state admin__state--pending">never used</span>
                )}
                {i.provider === 'email_otp' && (
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy !== null}
                    onClick={() =>
                      run(`code:${i.identifier}`, () =>
                        sendSignInCode(person.personId, i.identifier),
                      )
                    }
                  >
                    {busy === `code:${i.identifier}` ? 'Sending…' : 'Send a code'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="panel__note">
          Sign-in is a six-digit code, never a link (Safe Links would spend it).
        </p>
      </section>

      {/* ── PROFILE ─────────────────────────────────────────────────────────
          Where they sit, what they do, and how to reach them. Editable as of
          049; the work email joined it in 055 and the block moved above Roles
          at the same time — see the header for why.

          The chips below are org_unit_membership, which set_employment writes
          in the same transaction as the record above it. They are shown because
          they are what the ROSTER filters on — if they ever disagree with the
          record, that is the bug to chase, and this is the one screen where
          both are visible at once. */}
      <section className="panel__section">
        <h3 className="panel__h3">Profile</h3>

        {detail === null && !error && <p className="empty" aria-busy="true">Loading…</p>}

        {detail && canEditProfile && (
          <ProfileEditor
            detail={detail}
            orgUnits={profileUnits}
            colleagues={colleagues}
            externallyManaged={person.externallyManaged}
            canCreateOrgUnit={canCreateOrgUnit}
            onSaved={async () => {
              await onChanged()
              setDetail(await loadPersonDetail(person.personId))
            }}
          />
        )}

        {detail && detail.orgUnits.length > 0 && (
          <ul className="admin__chips">
            {detail.orgUnits.map((o) => (
              <li key={o.orgUnitId} className="chip">
                {o.name}
                <span className="chip__scope">{o.type}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── ROLES ──────────────────────────────────────────────────────────── */}
      <section className="panel__section">
        <h3 className="panel__h3">Roles</h3>

        {ROLE_GROUPS.map((group) => (
          <div key={group} className="panel__rolegroup">
            <h4 className="panel__h4">{group}</h4>
            <ul className="panel__roles">
              {GRANT_CATALOGUE.filter((r) => r.group === group).map((r) => {
                // Every grant of this kind — tenant-wide, scoped to an org
                // unit, or (store manager) one per store. A person can hold
                // several at once and each is removed separately: revoke_role
                // matches the org unit with `is not distinct from`, so removing
                // the tenant-wide grant deliberately leaves the scoped ones
                // standing.
                const held = person.grants.filter((g) => g.key === r.key)
                const token = `${person.personId}:${r.key}`

                if (r.kind === 'store') {
                  return (
                    <StoreRow
                      key={r.key}
                      role={r}
                      held={held}
                      stores={stores}
                      membershipActive={person.membershipActive}
                      lockedReason={
                        canPrivileged || canShopAdmin
                          ? null
                          : personLocked
                            ? '' // locked; the note above the profile says why, once
                            : null
                      }
                      busy={
                        busy && busy.startsWith(`${token}:`)
                          ? busy.slice(token.length + 1)
                          : null
                      }
                      onAdd={(storeId) =>
                        run(`${token}:add:${storeId}`, () => addRoomManager(storeId, person.personId))
                      }
                      onRemove={(storeId) =>
                        run(`${token}:remove:${storeId}`, () => removeRoomManager(storeId, person.personId))
                      }
                    />
                  )
                }

                const heldWide = held.some((g) => !g.scopeId)
                return (
                  <RoleRow
                    key={r.key}
                    role={r}
                    held={held}
                    typical={typicalHolders[r.key]}
                    orgUnits={scopeOptionsFor(r, orgUnits, scope)}
                    allowWide={!(regional && r.scopable)}
                    canRemoveScope={regional ? (id) => scope?.unitIds.includes(id) ?? false : undefined}
                    scopeLabel={
                      r.key === 'hr_admin'
                        ? 'Grant for a region — a country, legal entities or business units'
                        : regional
                          ? 'Grant for a part of your region'
                          : undefined
                    }
                    membershipActive={person.membershipActive}
                    lockedLastAdmin={
                      r.key === 'tenant_admin' && heldWide && activeAdminCount <= 1
                    }
                    lockedReason={
                      canPrivileged
                        ? null
                        : personLocked
                          ? '' // locked; the note above the profile says why, once
                          : r.privileged
                            ? `Only a Tenant Administrator can grant or remove ${r.name}.`
                            : null
                    }
                    busy={
                      busy && busy.startsWith(`${token}:`)
                        ? busy.slice(person.personId.length + 1)
                        : null
                    }
                    onGrant={(scopeId) =>
                      run(`${token}:grant:${scopeId ?? 'all'}`, () =>
                        assignRole(person.personId, r.key, scopeId),
                      )
                    }
                    // 159. One assign_role per unit, in turn. If one is refused
                    // the ones before it stand and are shown on the row at once,
                    // with the refusal in the server's words above them.
                    onGrantMany={(ids) =>
                      run(`${token}:grant:many`, async () => {
                        for (const id of ids) await assignRole(person.personId, r.key, id)
                      })
                    }
                    places={places}
                    onRevoke={(scopeId) =>
                      run(`${token}:revoke:${scopeId ?? 'all'}`, () =>
                        revokeRole(person.personId, r.key, scopeId),
                      )
                    }
                  />
                )
              })}
            </ul>
          </div>
        ))}

        {/* 140. Anything held that the catalogue has never heard of — a role
            added by a migration before anybody wrote its sentence. Shown, never
            dropped, and read-only: what it does is not written down here. */}
        {otherGrants.length > 0 && (
          <div className="panel__rolegroup">
            <h4 className="panel__h4">Other grants</h4>
            <ul className="admin__chips">
              {otherGrants.map((g) => (
                <li key={`${g.key}:${g.scopeId ?? 'all'}`} className="rolechip">
                  {g.name || g.key}
                  {g.scopeName && <span className="chip__scope">{g.scopeName}</span>}
                </li>
              ))}
            </ul>
            <p className="panel__note">
              Not described on this screen yet, so it cannot be changed here.
            </p>
          </div>
        )}

        <p className="panel__note">
          Everyone is an <strong>Employee</strong> already — that comes with
          being in the company and is not granted here.
          {!person.membershipActive &&
            ' Nothing can be granted while the membership is suspended: a role without a live membership does nothing.'}
        </p>
      </section>

      {/* ── ACCESS ─────────────────────────────────────────────────────────── */}
      <section className="panel__section panel__section--danger">
        <h3 className="panel__h3">Access</h3>

        {person.membershipActive ? (
          <>
            <p className="panel__note">
              Suspending removes access to everything. Their roles are kept, so
              they come back as what they were — this is the control for someone
              who has left or is away, not a way to change what they can do.
            </p>

            {isMe && (
              <p className="panel__rolecaution">
                You cannot suspend yourself — it would remove your ability to
                undo it.
              </p>
            )}
            {isLastAdmin && !isMe && (
              <p className="panel__rolecaution">
                This is the last active tenant administrator. Suspending them
                locks the tenant out of its own administration; appoint another
                first.
              </p>
            )}

            {!confirmSuspend ? (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={busy !== null || isMe || isLastAdmin || personLocked}
                onClick={() => setConfirmSuspend(true)}
              >
                Suspend access
              </button>
            ) : (
              <div className="panel__confirm">
                <label className="panel__reason">
                  <span>Why (recorded in the audit log)</span>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Left the company, long-term leave…"
                  />
                </label>
                <div className="panel__confirmrow">
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    disabled={busy !== null}
                    onClick={() =>
                      run('suspend', async () => {
                        await setMembershipActive(person.personId, false, reason)
                        setConfirmSuspend(false)
                        setReason('')
                      })
                    }
                  >
                    {busy === 'suspend' ? '…' : `Suspend ${person.displayName}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    onClick={() => setConfirmSuspend(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="panel__note">
              Inactive. They cannot sign in or read anything. Their roles were
              kept and come back with them, and what they posted stays up under
              their name.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busy !== null || personLocked}
              onClick={() =>
                run('reactivate', () => setMembershipActive(person.personId, true, reason))
              }
            >
              {busy === 'reactivate' ? '…' : 'Restore access'}
            </button>
          </>
        )}
      </section>

      <p className="roles__foot">
        Every change here is recorded in the audit log against you.
      </p>
    </div>
  )
}
